import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import { PATHS } from '../../paths';
import { useSidePanel } from '../../providers/SidePanelProvider';

/**
 * Open an alert in the assistant thread from a surface outside the assistant
 * pane — a record page's `RecordAlertBanner`.
 *
 * Two steps, because the pane sits in three places. Recording the alert and
 * opening the assistant is the whole move at the `xl+` column and the phone
 * sheet: the pane is mounted there and only has to open. Between `md` and
 * `xl` the pane is the `/assistant` route, so the click also has to navigate
 * there — and the alert survives that navigation because it is held on the
 * panel context rather than inside the pane.
 *
 * Surface first, then navigate: it also pre-arms the panel so a later widen
 * lands on the thread.
 *
 * @returns A click handler taking the alert's id and whether the row was still
 *   unread when it was clicked. The expand itself is issued by {@link useDrainPendingAlertExpand} once the pane
 *   is on screen.
 */
export function useOpenAlertInAssistant(): (alertId: string, wasUnread: boolean) => void {
  const navigate = useNavigate();
  const { openAlertInAssistant, assistantMountSite } = useSidePanel();

  return React.useCallback(
    (alertId: string, wasUnread: boolean) => {
      openAlertInAssistant(alertId, wasUnread);

      if (assistantMountSite === 'route') {
        void navigate(PATHS.ASSISTANT);
      }
    },
    [openAlertInAssistant, assistantMountSite, navigate]
  );
}
